/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* -------------- Engraving Text Limiter --------------- */
const engravingLimiter = function (string, limit) {
  var re = new RegExp(
    "[^a-zA-Z0-9 '\u00E9\u00E0\u00E8\u00F9\u00E2\u00EA\u00EE\u00F4\u00FB\u00EB\u00EF\u00FC\u00E7]*",
    "g",
  );
  var maxLength = limit > 0 ? limit : 12;
  string = string.replace(re, "").substring(0, maxLength);
  return string;
};
const formatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
});

function getUrlParameter(key) {
  return new URLSearchParams(window.location.search).get(key);
}

function observeElement(parentSelector, childSelector, callback, change) {
  const parent = document.querySelector(parentSelector);
  if (parent === null) return;

  new MutationObserver((_mutations, observer) => {
    const child = document.querySelector(childSelector);
    if (child || change === "removed") {
      observer.disconnect();
      callback(child);
    }
  }).observe(parent, { childList: true, subtree: true });
}

const loadImage = (src, maxWidth, maxHeight) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
      }
      if (width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
      }
      img.width = width;
      img.height = height;
      resolve(img);
    };
    img.onerror = () => reject(new Error(`Failed to load from ${src}`));
    img.src = src;
  });

// Canvas falls back to a default face if the webfont has not been fetched yet,
// so make sure it is ready before any text is drawn.
const loadFont = async (family, size) => {
  if (!document.fonts) return;
  try {
    await document.fonts.load(`${size || 20}px "${family}"`);
  } catch (err) {
    console.error(`Failed to load font ${family}`, err);
  }
};

async function renderPreview(selector) {
  const container = document.querySelector(selector);
  const canvas = container?.querySelector("canvas");
  if (!canvas) return;

  const textInput = container.querySelector("input,textarea");
  const imageInput = document.querySelector(
    `${selector} + .shopify-app-block input[name="properties[Gift Box Picture]"]`,
  );

  // Hide if neither field is set
  if (!textInput?.value && !imageInput) {
    canvas.style.display = "none";
    return;
  }

  const debugDraw = canvas.getAttribute("data-debug") === "true" ? true : false;

  const baseImageWidth = parseInt(canvas.getAttribute("data-base-width"));
  const baseImageHeight = parseInt(canvas.getAttribute("data-base-height"));

  const imageDx = parseInt(canvas.getAttribute("data-image-dx")) || 0;
  const imageDy = parseInt(canvas.getAttribute("data-image-dy")) || 0;
  const imageMaxWidth =
    parseInt(canvas.getAttribute("data-image-max-width")) || baseImageWidth;
  const imageMaxHeight =
    parseInt(canvas.getAttribute("data-image-max-width")) || baseImageHeight;
  const imageShape = canvas.getAttribute("data-image-shape") || "square";
  const imageFilter = canvas.getAttribute("data-image-filter") || "white";

  const textFont = canvas.getAttribute("data-text-font") || "Parisienne";
  const textSize = parseInt(canvas.getAttribute("data-text-size")) || 30;
  const textStyle = canvas.getAttribute("data-text-style") || "outline";
  const textVAlign = canvas.getAttribute("data-text-v-align") || "middle";
  const textHAlign = canvas.getAttribute("data-text-h-align") || "center";

  const textWidth = parseInt(canvas.getAttribute("data-text-width")) || 200;
  const textHeight = parseInt(canvas.getAttribute("data-text-height")) || 100;
  let textDx = parseInt(canvas.getAttribute("data-text-dx")) || 0;
  let textDy = parseInt(canvas.getAttribute("data-text-dy")) || 0;

  // Setup Context
  const ctx = canvas.getContext("2d");
  canvas.width = baseImageWidth;
  canvas.height = baseImageHeight;
  canvas.crossOrigin = "Anonymous";
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw Base Image
  const baseImageSrc = canvas.getAttribute("data-base-src-text");
  const defaultTextAlignAxis = canvas.getAttribute("data-text-align-axis") || "vertical";
  if (imageInput?.value) {
    const baseImageSrc = canvas.getAttribute("data-base-src-image");
  } else {
    if (defaultTextAlignAxis === "vertical") {
      textDx = 0;
    } else {
      textDy = 0;
    }
  }

  if (!baseImageSrc) return;
  const baseImage = await loadImage(
    baseImageSrc,
    baseImageWidth,
    baseImageHeight,
  );
  if (!baseImage) return;
  ctx.drawImage(baseImage, 0, 0);

  // Draw Image
  if (imageInput?.value) {
    const image = await loadImage(
      imageInput.value,
      imageMaxWidth,
      imageMaxHeight,
    );
    const imageX = ctx.canvas.width / 2 - image.width / 2 + imageDx;
    const imageY = ctx.canvas.height / 2 - image.height / 2 + imageDy;

    if (imageFilter === "brown") {
      //ctx.globalCompositeOperation = "multiply";
      ctx.globalAlpha = 0.9;
      ctx.filter = "saturate(0.8) contrast(0.8) brightness(0.8)";
    }
    if (imageShape === "circle") {
      const radius = Math.min(imageMaxWidth, imageMaxHeight) / 2;
      ctx.save();
      ctx.beginPath();
      ctx.arc(imageX + radius, imageY + radius, radius, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(image, imageX, imageY, imageMaxWidth, imageMaxHeight);
      ctx.restore();
    } else {
      ctx.drawImage(image, imageX, imageY, imageMaxWidth, imageMaxHeight);
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.filter = "none";
    ctx.globalAlpha = 1;

    if (imageFilter === "brown") {
      ctx.save();
      ctx.strokeStyle = "black";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      if (imageShape === "circle") {
        const radius = Math.min(imageMaxWidth, imageMaxHeight) / 2;
        ctx.arc(imageX + radius, imageY + radius, radius, 0, Math.PI * 2);
      } else {
        ctx.roundRect(imageX, imageY, imageMaxWidth, imageMaxHeight, 4);
      }
      ctx.stroke();
      ctx.restore();
    }

    if (debugDraw) {
      ctx.strokeRect(imageX, imageY, imageMaxWidth, imageMaxHeight);
    }
  }

  // Draw Text
  await loadFont(textFont, textSize);
  const { drawText } = window.canvasTxt;
  const text = textInput?.value || "";
  drawText(ctx, text, {
    x: canvas.width / 2 - textWidth / 2 + textDx,
    y: canvas.height / 2 - textHeight / 2 + textDy,
    debug: debugDraw,
    width: textWidth,
    height: textHeight,
    drawStyle: textStyle,
    font: textFont,
    fontSize: textSize,
    vAlign: textVAlign,
    align: textHAlign,
  });

  canvas.style.display = "block";
}

function setupVariant(variant, field, activeValues) {
  const match = variant.options.some((item) => activeValues.includes(item));
  if (match) {
    field.style.display = "block";
    const input = field.querySelector("input,textarea");
    if (!input) return;
    input.removeAttribute("disabled");
  } else {
    field.style.display = "none";
    const input = field.querySelector("input,textarea");
    if (!input) return;
    input.value = "";
    input.setAttribute("disabled", "disabled");
  }
}

function setupToggleField(config) {
  const { field, options, radioValues, activeValues, onInput } = config;

  if (!field) return;

  const variantPicker = field
    .closest(".product-form")
    .querySelector("variant-picker");

  const variant = _selectedVariant.get(variantPicker);
  setupVariant(variant, field, activeValues);

  if (onInput) field.addEventListener("input", onInput);

  document.addEventListener("variant:changed", function (event) {
    const variant = event.detail.variant;
    setupVariant(variant, field, activeValues);
  });
}

const handleAddToCartSubmit = (e) => {
  e.preventDefault();
  const data = new FormData(e.target);
  let properties = {};

  e.target.querySelectorAll("input[data-properties]").forEach((property) => {
    if (data.get(property.name)) {
      properties[property.name] = data.get(property.name);
      data.delete(property.name);
    }
  });

  const formJSON = Object.fromEntries(data.entries());
  formJSON.properties = properties;

  console.log(JSON.stringify(formJSON));

  fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formJSON),
  }).then((response) => {
    if (response.ok) {
      window.location.href = "/cart";
    }
  });
};

function intialiseAddToCartForm() {
  const addToCartForm = document.getElementById("add-to-cart-form");

  if (addToCartForm) {
    addToCartForm.addEventListener("submit", handleAddToCartSubmit);
  }
}

const uploadLiftTextSelector = ".upload-lift-text-js";
const productEngravingSelector = ".product-engraving-js";

const handleEngravingInput = (e) => {
  // Inside a personalisation block the limit is a template setting, applied to the input as
  // maxlength. Everywhere else keep the original hard cap of 12 so existing templates are
  // unaffected, even the ones whose line item property block asks for more.
  const limit = e.target.closest("[data-personalisation]")
    ? e.target.maxLength
    : 0;

  e.target.value = engravingLimiter(e.target.value, limit);
  renderPreview(productEngravingSelector);
};

const fieldConfigs = [
  {
    field: document.querySelector(productEngravingSelector),
    options: [
      "Engraving",
      "Extras",
      "Engrave your Razor",
      "Personalised Handle",
      "Personalised Razor",
      "Personalised Razor &amp; Tin",
      "Make it Yours",
    ],
    radioValues: [
      "No Extras",
      "Add Engraving",
      "None",
      "No Engraving",
      "Classic Razor",
    ],
    activeValues: [
      "Add Engraving",
      "Engraving",
      "Engraved Razor",
      "FREE Engraving",
      "FREE Engraving (Worth £6)",
    ],
    onInput: handleEngravingInput,
  },
  {
    field: document.querySelector(uploadLiftTextSelector),
    options: ["Gift Box Greeting"],
    radioValues: [
      "No Message",
      "Add Message",
      "Add Message & Photo",
      "FREE Message (Worth £6)",
      "FREE Message & Photo (Worth £6)",
    ],
    activeValues: [
      "Add Message",
      "Add Message & Photo",
      "FREE Message (Worth £6)",
      "FREE Message & Photo (Worth £6)",
    ],
    onInput: () => {
      renderPreview(uploadLiftTextSelector);
    },
  },
];

function observeAdded() {
  observeElement(
    "div[data-upload-lift]",
    'input[name="properties[Gift Box Picture]"]',
    () => {
      renderPreview(uploadLiftTextSelector);
      observeRemoved();
    },
    "added",
  );
}

function observeRemoved() {
  observeElement(
    "div[data-upload-lift]",
    'input[name="properties[Gift Box Picture]"]',
    () => {
      renderPreview(uploadLiftTextSelector);
      observeAdded();
    },
    "removed",
  );
}

fieldConfigs.forEach(setupToggleField);
observeAdded();
intialiseAddToCartForm();

const dynamicRedirect = getUrlParameter("dynamic_redirect");
if (dynamicRedirect) {
  window.location.href = dynamicRedirect;
}

(function () {
  const STORAGE_KEY = "ffs_referral_discount";
  const NEW_HREF =
    "/pages/subscribe/razor-trial-kit-offer-postage";
  const NEW_TEXT = "Apply Discount";

  function patchAnchor(a) {
    const anchorUrl = new URL(a.href, window.location.origin);

    // Code lives in the path: /discount/{code} — fall back to ?rc_code= if needed
    const pathMatch = anchorUrl.pathname.match(/\/discount\/([^/]+)/);
    const code = pathMatch ? pathMatch[1] : anchorUrl.searchParams.get("rc_code");
    const flowId = anchorUrl.searchParams.get("rc_flow_id");

    if (!code) {
      console.warn("[FFS] No discount code found on anchor:", a.href);
      return;
    }

    const url = new URL(`https://www.ffs.co.uk/discount/${code}`);
    url.searchParams.set("redirect", NEW_HREF);
    url.searchParams.set("code", code);
    url.searchParams.set("utm_source", "referral");
    url.searchParams.set("utm_medium", "landing_page");
    if (flowId) url.searchParams.set("rc_flow_id", flowId);

    a.href = url.toString();
    a.textContent = NEW_TEXT;
    a.removeAttribute("target");
    a.dataset.ffsPatchedDiscount = code;
    console.log("[FFS] Anchor patched →", a.href);
  }

   const existing = document.querySelector('a[href*="/discount/"][role="button"]');
   if (existing) {
     patchAnchor(existing);
   } else {
     observeElement('body', 'a[href*="/discount/"][role="button"]', patchAnchor, "added");
   }
})();

observeElement('body', 'ul.sib-sms-select__list > li:nth-child(240)', (elem) => {
  elem.click();
}, "added");